import React, { useState, useEffect, useCallback } from 'react';
import { ticketsAPI } from '../../services/api';
import { formatInTz } from '../../utils/timezone';
import { T } from '../../constants/mockData';
import { toast } from '../../utils/toast';

const STATUS_COLORS = {
  pending:  { bg: '#FEF3C7', color: '#92400E', dot: '#F59E0B' },
  approved: { bg: '#D1FAE5', color: '#065F46', dot: '#10B981' },
  rejected: { bg: '#FEE2E2', color: '#991B1B', dot: '#EF4444' },
};

export default function PauseRequestsAdmin() {
  const [requests, setRequests]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [actionLoading, setActionLoading] = useState(null); // reqId being actioned
  const [noteModal, setNoteModal]     = useState(null); // { id, action, note }
  const [refreshing, setRefreshing]   = useState(false);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    else setRefreshing(true);
    try {
      const rows = await ticketsAPI.getPendingPauseRequests();
      setRequests(rows || []);
    } catch (e) {
      console.error('Failed to load pause requests:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAction = useCallback(async (reqId, action, note = '') => {
    setActionLoading(reqId + action);
    try {
      if (action === 'approve') {
        await ticketsAPI.approvePauseRequest(reqId, note);
        window.dispatchEvent(new CustomEvent('sysnet-pause-approved'));
      } else {
        await ticketsAPI.rejectPauseRequest(reqId, note);
        window.dispatchEvent(new CustomEvent('sysnet-pause-rejected'));
      }
      await load(true);
    } catch (e) {
      toast(e.message || 'Action failed');
    } finally {
      setActionLoading(null);
      setNoteModal(null);
    }
  }, [load]);

  const filtered = filterStatus === 'all'
    ? requests
    : requests.filter(r => r.status === filterStatus);

  const counts = {
    pending:  requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
  };

  const thStyle = {
    padding: '0 14px', fontSize: 11, fontWeight: 700, color: T.muted,
    textAlign: 'left', height: 38, background: '#F8FAFC',
    borderBottom: `1.5px solid ${T.border}`, whiteSpace: 'nowrap',
    textTransform: 'uppercase', letterSpacing: '0.05em',
  };
  const tdStyle = {
    padding: '12px 14px', fontSize: 13, color: '#1A202C',
    borderBottom: `1px solid ${T.border}`, verticalAlign: 'middle',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#1A202C', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 20 }}>⏸</span> Timer Pause Requests
          </div>
          <div style={{ fontSize: 13, color: T.muted, marginTop: 3 }}>
            Review and approve or reject agent timer pause requests
          </div>
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          style={{
            height: 34, padding: '0 14px', borderRadius: 8,
            border: `1px solid ${T.border}`, background: '#fff',
            fontSize: 13, fontWeight: 600, color: T.muted,
            cursor: refreshing ? 'wait' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <span style={{ display: 'inline-block', animation: refreshing ? 'spin 1s linear infinite' : 'none' }}>↻</span>
          Refresh
        </button>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexShrink: 0 }}>
        {[
          { key: 'pending',  label: 'Pending',  icon: '⏳' },
          { key: 'approved', label: 'Approved', icon: '✅' },
          { key: 'rejected', label: 'Rejected', icon: '❌' },
        ].map(({ key, label, icon }) => {
          const c = STATUS_COLORS[key];
          const active = filterStatus === key;
          return (
            <button
              key={key}
              onClick={() => setFilterStatus(key)}
              style={{
                flex: 1, padding: '14px 16px', borderRadius: 12,
                border: `2px solid ${active ? c.dot : T.border}`,
                background: active ? c.bg : '#fff',
                cursor: 'pointer', textAlign: 'left',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ fontSize: 22, marginBottom: 6 }}>{icon}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: active ? c.color : '#1A202C' }}>
                {counts[key]}
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: active ? c.color : T.muted }}>
                {label}
              </div>
            </button>
          );
        })}
        <button
          onClick={() => setFilterStatus('all')}
          style={{
            flex: 1, padding: '14px 16px', borderRadius: 12,
            border: `2px solid ${filterStatus === 'all' ? T.active : T.border}`,
            background: filterStatus === 'all' ? T.g50 : '#fff',
            cursor: 'pointer', textAlign: 'left',
            transition: 'all 0.15s',
          }}
        >
          <div style={{ fontSize: 22, marginBottom: 6 }}>📋</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: filterStatus === 'all' ? T.active : '#1A202C' }}>
            {requests.length}
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: filterStatus === 'all' ? T.active : T.muted }}>
            All Requests
          </div>
        </button>
      </div>

      {/* Table */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', border: `1px solid ${T.border}`, borderRadius: 12, background: '#fff' }}>
        {loading ? (
          <div style={{ padding: '60px 0', textAlign: 'center', color: T.muted, fontSize: 14 }}>
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '60px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>⏸</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1A202C' }}>No {filterStatus === 'all' ? '' : filterStatus} pause requests</div>
            <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
              {filterStatus === 'pending' ? 'All caught up! No requests waiting for approval.' : 'Nothing here yet.'}
            </div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
              <tr>
                <th style={thStyle}>Ticket #</th>
                <th style={thStyle}>Subject</th>
                <th style={thStyle}>Requested By</th>
                <th style={thStyle}>Reason</th>
                <th style={thStyle}>Requested At</th>
                <th style={thStyle}>Status</th>
                {filterStatus !== 'pending' && <th style={thStyle}>Reviewed By</th>}
                {filterStatus !== 'pending' && <th style={thStyle}>Review Note</th>}
                {filterStatus === 'pending' || filterStatus === 'all' ? <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const c = STATUS_COLORS[r.status] || STATUS_COLORS.pending;
                const isPending = r.status === 'pending';
                return (
                  <tr key={r.id} style={{ background: '#fff', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                  >
                    {/* Ticket # */}
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: 700, color: T.active, fontSize: 12 }}>
                        {r.ticket_number || `#${r.ticket_id}`}
                      </span>
                    </td>

                    {/* Subject */}
                    <td style={{ ...tdStyle, maxWidth: 260 }}>
                      <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                        {r.subject || '—'}
                      </span>
                    </td>

                    {/* Requested by */}
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: T.g100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: T.active, flexShrink: 0 }}>
                          {(r.requested_by_name || r.requested_by_email || '?').slice(0, 2).toUpperCase()}
                        </div>
                        <span style={{ fontWeight: 600 }}>{r.requested_by_name || r.requested_by_email || '—'}</span>
                      </div>
                    </td>

                    {/* Reason */}
                    <td style={{ ...tdStyle, maxWidth: 200 }}>
                      <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '4px 8px', fontSize: 12, color: '#78350F', display: 'inline-block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        "{r.remark || '—'}"
                      </div>
                    </td>

                    {/* Requested At */}
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontSize: 12, color: T.muted }}>
                      {r.created_at ? formatInTz(r.created_at, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>

                    {/* Status badge */}
                    <td style={tdStyle}>
                      <span style={{ background: c.bg, color: c.color, fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 99, display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.dot }} />
                        {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                      </span>
                    </td>

                    {/* Reviewed By (non-pending filter) */}
                    {filterStatus !== 'pending' && (
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontSize: 12 }}>
                        {r.reviewed_by_name || '—'}
                      </td>
                    )}

                    {/* Review Note (non-pending filter) */}
                    {filterStatus !== 'pending' && (
                      <td style={{ ...tdStyle, maxWidth: 180, fontSize: 12, color: T.muted }}>
                        {r.review_note || '—'}
                      </td>
                    )}

                    {/* Actions */}
                    {(filterStatus === 'pending' || filterStatus === 'all') && (
                      <td style={{ ...tdStyle, textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {isPending ? (
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                            <button
                              title="Approve"
                              onClick={() => setNoteModal({ id: r.id, action: 'approve', note: '' })}
                              disabled={!!actionLoading}
                              style={{
                                width: 32, height: 32, borderRadius: 7,
                                border: '1.5px solid #10B981', background: '#D1FAE5',
                                color: '#065F46', fontSize: 16, fontWeight: 700,
                                cursor: actionLoading ? 'wait' : 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all 0.15s',
                              }}
                            >
                              ✓
                            </button>
                            <button
                              title="Reject"
                              onClick={() => setNoteModal({ id: r.id, action: 'reject', note: '' })}
                              disabled={!!actionLoading}
                              style={{
                                width: 32, height: 32, borderRadius: 7,
                                border: '1.5px solid #EF4444', background: '#FEE2E2',
                                color: '#991B1B', fontSize: 16, fontWeight: 700,
                                cursor: actionLoading ? 'wait' : 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all 0.15s',
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <span style={{ fontSize: 12, color: T.muted }}>—</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Approve / Reject note modal */}
      {noteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 420, maxWidth: '94vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1A202C', marginBottom: 6 }}>
              {noteModal.action === 'approve' ? '✅ Approve Pause Request' : '❌ Reject Pause Request'}
            </div>
            <div style={{ fontSize: 13, color: T.muted, marginBottom: 16 }}>
              {noteModal.action === 'approve'
                ? 'The timer will be paused immediately. Optionally add a note.'
                : 'The timer will continue running. Optionally add a reason for rejection.'}
            </div>
            <textarea
              autoFocus
              placeholder={noteModal.action === 'approve' ? 'Approval note (optional)…' : 'Rejection reason (optional)…'}
              value={noteModal.note}
              onChange={e => setNoteModal(m => ({ ...m, note: e.target.value }))}
              rows={3}
              style={{
                width: '100%', padding: '10px 12px', border: `1.5px solid ${T.border}`,
                borderRadius: 8, fontSize: 13, color: '#1A202C', outline: 'none',
                resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
                background: '#F8FAFC',
              }}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setNoteModal(null)}
                style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${T.border}`, background: '#fff', fontSize: 13, fontWeight: 600, color: T.muted, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleAction(noteModal.id, noteModal.action, noteModal.note)}
                disabled={actionLoading === noteModal.id + noteModal.action}
                style={{
                  padding: '8px 20px', borderRadius: 8, border: 'none',
                  background: noteModal.action === 'approve' ? '#10B981' : '#EF4444',
                  color: '#fff', fontSize: 13, fontWeight: 700,
                  cursor: actionLoading ? 'wait' : 'pointer',
                }}
              >
                {actionLoading === noteModal.id + noteModal.action
                  ? 'Processing…'
                  : noteModal.action === 'approve' ? 'Confirm Approve' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
